import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { Ruler, Home } from 'lucide-react';

const Step1RoomBasics = ({ formData, updateFormData, unitSystem }) => {
  const roomArea = formData.roomLength && formData.roomWidth 
    ? (parseFloat(formData.roomLength) * parseFloat(formData.roomWidth)).toFixed(2)
    : 0;
    
  const roomVolume = formData.roomLength && formData.roomWidth && formData.roomHeight
    ? (parseFloat(formData.roomLength) * parseFloat(formData.roomWidth) * parseFloat(formData.roomHeight)).toFixed(2)
    : 0;
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Room Dimensions</h2>
        <p className="text-gray-600">Enter the basic measurements of your room</p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="roomLength" className="text-base font-medium">
            Room Length (meters) *
          </Label>
          <div className="relative">
            <Input
              id="roomLength"
              type="number"
              step="0.1"
              min="1"
              max="50"
              value={formData.roomLength}
              onChange={(e) => updateFormData('roomLength', e.target.value)}
              placeholder="e.g., 5.0"
              className="pl-10 h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500"
            />
            <Ruler className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="roomWidth" className="text-base font-medium">
            Room Width (meters) *
          </Label>
          <div className="relative">
            <Input
              id="roomWidth"
              type="number"
              step="0.1"
              min="1"
              max="50"
              value={formData.roomWidth}
              onChange={(e) => updateFormData('roomWidth', e.target.value)}
              placeholder="e.g., 4.0"
              className="pl-10 h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500"
            />
            <Ruler className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
          </div>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="roomHeight" className="text-base font-medium">
          Room Height (meters) *
        </Label>
        <Select value={formData.roomHeight} onValueChange={(value) => updateFormData('roomHeight', value)}>
          <SelectTrigger className="h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500">
            <SelectValue placeholder="Select room height" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="2.4">2.4m (Standard)</SelectItem>
            <SelectItem value="2.7">2.7m (Common)</SelectItem>
            <SelectItem value="3.0">3.0m (High Ceiling)</SelectItem>
            <SelectItem value="3.5">3.5m (Very High)</SelectItem>
            <SelectItem value="custom">Custom Height</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="roomType" className="text-base font-medium">
          Room Type
        </Label>
        <Select value={formData.roomType} onValueChange={(value) => updateFormData('roomType', value)}>
          <SelectTrigger className="h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500">
            <SelectValue placeholder="Select room type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bedroom">Bedroom</SelectItem>
            <SelectItem value="living-room">Living Room</SelectItem>
            <SelectItem value="kitchen">Kitchen</SelectItem>
            <SelectItem value="office">Office</SelectItem>
            <SelectItem value="commercial">Commercial Space</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Auto-calculated Display */}
      {roomArea > 0 && (
        <Card className="p-4 bg-blue-50 border-blue-200 animate-fadeIn">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 font-medium">Room Area</p>
              <p className="text-2xl font-bold text-blue-900">{roomArea} m²</p>
            </div>
            {roomVolume > 0 && (
              <div className="text-right">
                <p className="text-sm text-blue-700 font-medium">Room Volume</p>
                <p className="text-2xl font-bold text-blue-900">{roomVolume} m³</p>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default Step1RoomBasics;